import { AuthStorage } from "utils/auth-storage";
import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Input,
  Empty,
  theme,
  Button,
  Badge,
  Modal,
  Tooltip,
  message,
  Spin,
} from "antd";
import {
  FolderFilled,
  SearchOutlined,
  CaretRightOutlined,
  HolderOutlined,
  PlusOutlined,
  DeleteOutlined,
  DeleteFilled,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  defaultDropAnimationSideEffects,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import SortableSubdivisionPanel from "./sortable-subdivision-panel";
import SortableRow from "./sortable-row";
import ReviewPDFModals from "./review-pdf-modals";
import { HilyteService } from "apis/Services/HilyteService";
import { useParams } from "react-router-dom";
import LoadingComponent from "components/loading-component";

// --- CONSTANTS ---
const DIVISIONS_2020 = {
  "01": "General Requirements",
  "02": "Existing Conditions",
  "03": "Concrete",
  "04": "Masonry",
  "05": "Metals",
  "06": "Wood, Plastics, Composites",
  "07": "Thermal and Moisture Protection",
  "08": "Openings",
  "09": "Finishes",
  10: "Specialties",
  11: "Equipment",
  12: "Furnishings",
  13: "Special Construction",
  14: "Conveying Equipment",
  21: "Fire Suppression",
  22: "Plumbing",
  23: "HVAC",
  25: "Integrated Automation",
  26: "Electrical",
  27: "Communications",
  28: "Electronic Safety & Security",
  31: "Earthwork",
  32: "Exterior Improvements",
  33: "Utilities",
  34: "Transportation",
  35: "Waterway & Marine Work",
  40: "Process Integration",
  41: "Material Processing and Handling Equipment",
  42: "Process Heating, Cooling, and Drying Equipment",
  43: "Process Gas and Liquid Handling, Purification, and Storage Equipment",
  44: "Pollution Control Equipment",
  45: "Industry-Specific Manufacturing Equipment",
  46: "Water and Wastewater Equipment",
  48: "Electrical Power Generation",
};

const DIVISIONS_2016 = {
  "00": "Procurement and Contracting Requirements",
  "01": "General Requirements",
  "02": "Existing Conditions",
  "03": "Concrete",
  "04": "Masonry",
  "05": "Metals",
  "09": "Finishes",
  10: "Specialties",
  11: "Equipment",
  12: "Furnishings",
  14: "Conveying Systems",
  15: "Mechanical",
  16: "Electrical",
  27: "Communications",
  "40-49": "Process Equipment",
};

// --- HELPERS ---

/**
 * Normalize a raw item into a standard shape.
 * `forcePending` controls the isPending value:
 *   - true  → always isPending: true  (cursor / analyze-page results)
 *   - false → preserve the item's own isPending (full-PDF / stored results)
 */
const normalizeItem = (item, forcePending = false) => ({
  ...item,
  dndId: item.dndId || crypto.randomUUID(),
  isPending: forcePending ? true : item.isPending !== false, // preserve false strictly, default true
});

/**
 * Returns a stable string key used to detect duplicate submittals.
 * Priority: backend id → specNumber+specTitle composite → dndId (last resort).
 */
const itemKey = (item) => {
  if (item.id) return `id:${item.id}`;
  const num = (item.specNumber || "").trim().toLowerCase();
  const title = (item.specTitle || "").trim().toLowerCase();
  if (num || title) return `spec:${num}|${title}`;
  return `dnd:${item.dndId}`;
};

/**
 * Process a raw data object into a normalized state shape.
 * `forcePending` is forwarded to normalizeItem.
 */
const processData = (rawData, forcePending = false) => {
  if (!rawData || typeof rawData !== "object") return {};

  const processed = {};
  Object.keys(rawData).forEach((divKey) => {
    const divisionData = rawData[divKey];
    if (!divisionData || typeof divisionData !== "object") return;

    processed[divKey] = {};
    Object.keys(divisionData).forEach((subKey) => {
      const items = divisionData[subKey];
      processed[divKey][subKey] = Array.isArray(items)
        ? items.map((item) => normalizeItem(item, forcePending))
        : [];
    });
  });

  return processed;
};

/**
 * Merge `newData` into `existingData` without duplicating items.
 * Duplicate detection uses `itemKey()` which checks id, then specNumber+specTitle.
 * Existing items are never mutated — only new (non-duplicate) items are appended.
 */
const mergeDataWithoutDuplicates = (existingData, newData) => {
  const merged = { ...existingData };

  Object.keys(newData).forEach((divKey) => {
    const currentDivData = merged[divKey] ? { ...merged[divKey] } : {};
    let divisionChanged = !merged[divKey];

    Object.keys(newData[divKey]).forEach((subKey) => {
      const newItems = newData[divKey][subKey];
      const existingItems = currentDivData[subKey]
        ? [...currentDivData[subKey]]
        : [];

      // Build a Set of keys that already exist in this subdivision
      const existingKeys = new Set(existingItems.map(itemKey));

      const itemsToAdd = newItems.filter(
        (newItem) => !existingKeys.has(itemKey(newItem)),
      );

      if (itemsToAdd.length > 0) {
        currentDivData[subKey] = [...existingItems, ...itemsToAdd];
        divisionChanged = true;
      } else if (!currentDivData[subKey]) {
        currentDivData[subKey] = existingItems;
        divisionChanged = true;
      }
    });

    if (divisionChanged) {
      merged[divKey] = currentDivData;
    }
  });

  return merged;
};

export default function ReviewPDF({
  data,
  requestsData,
  tableState,
  setTableState,
  masterFormat,
  isOverwrite,
  isCursorMode,
  pdfID,
  existingSections,
}) {
  const { token } = theme.useToken();
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [activeSubdivisionKeys, setActiveSubdivisionKeys] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [searchText, setSearchText] = useState("");
  const [isLoadingSubmittals, setIsLoadingSubmittals] = useState(false);

  const { company_id } = useParams();

  const [activeDragItem, setActiveDragItem] = useState(null);

  const [isAddSubdivisionModalOpen, setIsAddSubdivisionModalOpen] =
    useState(false);
  const [newSubdivisionName, setNewSubdivisionName] = useState("");
  const [isAddSubmittalModalOpen, setIsAddSubmittalModalOpen] = useState(false);
  const [newSubmittalName, setNewSubmittalName] = useState("");
  const [subdivisionForNewSubmittal, setSubdivisionForNewSubmittal] =
    useState(null);
  const [pendingDeleteSubdivision, setPendingDeleteSubdivision] =
    useState(null);

  const divisions = useMemo(() => {
    return masterFormat === "2016" ? DIVISIONS_2016 : DIVISIONS_2020;
  }, [masterFormat]);

  const panelStyle = {
    background: "#FAFAFA",
    borderRadius: token.borderRadiusLG,
    border: `1px solid ${token.colorBorderSecondary}`,
    borderColor: token.colorBorderSecondary,
  };

  useEffect(() => {
    if (!data || Object.keys(data).length === 0) return;

    // Preserve each item's own isPending (false = already accepted, true = pending)
    const processedData = processData(data, false);

    setTableState((prevState) => {
      // isCursorMode means cursor / analyze-page triggered this — skip
      if (isCursorMode) return prevState;

      const shouldOverwrite = isOverwrite === "1";

      if (shouldOverwrite || Object.keys(prevState).length === 0) {
        return processedData;
      }

      return mergeDataWithoutDuplicates(prevState, processedData);
    });
  }, [data]);

  useEffect(() => {
    if (!requestsData || Object.keys(requestsData).length === 0) return;

    // Force isPending:true — these are freshly found, unreviewed submittals
    const processedRequests = processData(requestsData, true);

    setTableState((prevState) => {
      const nextState =
        Object.keys(prevState).length === 0
          ? processedRequests
          : mergeDataWithoutDuplicates(prevState, processedRequests);

      Promise.resolve().then(() => {
        handleSaveLatestSubmittals(nextState);
      });

      return nextState;
    });
  }, [requestsData]);

  useEffect(() => {
    if (!selectedDivision && Object.keys(tableState).length > 0) {
      const firstDivisionKey =
        Object.keys(tableState).find(
          (key) => Object.keys(tableState[key] || {}).length > 0,
        ) || Object.keys(divisions)[0];

      if (firstDivisionKey) {
        setSelectedDivision(firstDivisionKey);
        setActiveSubdivisionKeys(
          Object.keys(tableState[firstDivisionKey] || {}),
        );
      }
    }
  }, [tableState, selectedDivision]);

  // --- DERIVED STATE ---
  const currentDivisionData = selectedDivision
    ? tableState[selectedDivision] || {}
    : {};

  const subdivisionKeys = useMemo(
    () => Object.keys(currentDivisionData),
    [currentDivisionData],
  );

  // --- SENSORS (DND) ---
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // --- HANDLERS ---
  const handleDivisionClick = (code) => {
    const divisionKey =
      Object.keys(tableState).find((key) => key.startsWith(code)) || code;
    setSelectedDivision(divisionKey);
    const currentData = tableState[divisionKey] || {};
    setActiveSubdivisionKeys(Object.keys(currentData));
  };

  const toggleSubdivision = (key) => {
    setActiveSubdivisionKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleCellClick = (path, field, value) => {
    setEditingCell({ path, field });
    setEditValue(value || "");
  };

  const handleCellSave = () => {
    if (!editingCell) return;
    const { path, field } = editingCell;
    const [division, subdivision, submittalId] = path;

    if (
      submittalId === undefined &&
      field === "name" &&
      subdivision &&
      editValue
    ) {
      setTableState((prev) => {
        const newState = { ...prev };
        const divData = newState[division];
        const oldData = divData[subdivision];

        const newDivData = {};
        Object.keys(divData).forEach((key) => {
          if (key === subdivision) newDivData[editValue] = oldData;
          else newDivData[key] = divData[key];
        });

        newState[division] = newDivData;
        handleSaveLatestSubmittals(newState);
        return newState;
      });
      setActiveSubdivisionKeys((prev) =>
        prev.map((key) => (key === subdivision ? editValue : key)),
      );
    } else if (submittalId !== undefined) {
      setTableState((prev) => {
        const newState = { ...prev };
        const list = newState[division][subdivision];
        const itemIndex = list.findIndex((i) => i.dndId === submittalId);
        if (itemIndex > -1) list[itemIndex][field] = editValue;
        handleSaveLatestSubmittals(newState);
        return newState;
      });
    }
    setEditingCell(null);
    setEditValue("");
  };

  // --- DND LOGIC ---
  const getSubmittalDataById = (id) => {
    if (!selectedDivision || !tableState[selectedDivision]) return null;
    for (const subKey in tableState[selectedDivision]) {
      const submittal = tableState[selectedDivision][subKey].find(
        (item) => item.dndId === id,
      );
      if (submittal) return submittal;
    }
    return null;
  };

  const findSubdivisionOfSubmittal = (id) => {
    for (const subKey of subdivisionKeys) {
      const submittals = currentDivisionData[subKey];
      if (submittals.find((item) => item.dndId === id)) return subKey;
    }
    return null;
  };

  const handleDragStart = (event) => {
    const active = event.active;
    const type = active.data.current?.type;

    if (type === "submittal") {
      const submittalData = getSubmittalDataById(active.id);
      setActiveDragItem({
        id: active.id,
        type,
        display: submittalData?.specTitle || "Unknown Submittal",
      });
    } else if (type === "subdivision") {
      setActiveDragItem({ id: active.id, type, display: active.id });
    }
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;
    const activeId = active.id;
    const overId = over.id;

    if (active.data.current?.type !== "submittal") return;

    const activeContainer = findSubdivisionOfSubmittal(activeId);
    const overContainer =
      over.data.current?.type === "submittal"
        ? findSubdivisionOfSubmittal(overId)
        : over.data.current?.subdivisionName;

    if (!activeContainer || !overContainer || activeContainer === overContainer)
      return;

    setTableState((prevState) => {
      const newState = { ...prevState };
      const divisionData = { ...newState[selectedDivision] };
      const sourceList = [...divisionData[activeContainer]];
      const destList = [...divisionData[overContainer]];

      const activeItemIndex = sourceList.findIndex((i) => i.dndId === activeId);
      const [movedItem] = sourceList.splice(activeItemIndex, 1);

      let newIndex = destList.length;
      if (over.data.current?.type === "submittal") {
        const overItemIndex = destList.findIndex((i) => i.dndId === overId);
        if (overItemIndex >= 0) newIndex = overItemIndex;
      }

      destList.splice(newIndex, 0, movedItem);
      divisionData[activeContainer] = sourceList;
      divisionData[overContainer] = destList;
      newState[selectedDivision] = divisionData;

      return newState;
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveDragItem(null);
    if (!over || active.id === over.id) return;

    const type = active.data.current?.type;

    if (type === "subdivision") {
      setTableState((prev) => {
        const oldKeys = Object.keys(prev[selectedDivision]);
        const oldIndex = oldKeys.indexOf(active.id);
        const newIndex = oldKeys.indexOf(over.id);
        const newKeys = arrayMove(oldKeys, oldIndex, newIndex);

        const newDivisionData = {};
        newKeys.forEach(
          (k) => (newDivisionData[k] = prev[selectedDivision][k]),
        );

        return { ...prev, [selectedDivision]: newDivisionData };
      });
    } else if (type === "submittal") {
      const container = findSubdivisionOfSubmittal(active.id);
      if (container) {
        setTableState((prev) => {
          const list = prev[selectedDivision][container];
          const oldIndex = list.findIndex((i) => i.dndId === active.id);
          const newIndex = list.findIndex((i) => i.dndId === over.id);
          return {
            ...prev,
            [selectedDivision]: {
              ...prev[selectedDivision],
              [container]: arrayMove(list, oldIndex, newIndex),
            },
          };
        });
      }
    }
  };

  const filteredDivisions = useMemo(() => {
    const lowerCaseSearch = searchText.toLowerCase();
    return Object.entries(divisions)
      .filter(([code, name]) => {
        return (
          code.toLowerCase().includes(lowerCaseSearch) ||
          name.toLowerCase().includes(lowerCaseSearch)
        );
      })
      .sort((a, b) => Number(a[0]) - Number(b[0]));
  }, [searchText]);

  const EditableCell = ({ value, path, field, className = "" }) => {
    const isEditing =
      editingCell?.path?.join("-") === path.join("-") &&
      editingCell?.field === field;
    const isSubdivisionNameCell = path.length === 2 && field === "name";

    if (isEditing) {
      return (
        <Input
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleCellSave}
          onPressEnter={handleCellSave}
          autoFocus
          className="border border-1 p-1"
          style={{
            margin: isSubdivisionNameCell ? "-11px 0" : "-5px -11px",
            padding: isSubdivisionNameCell ? "11px 0" : "5px 11px",
            width: isSubdivisionNameCell ? "100%" : "auto",
            boxShadow: "none",
          }}
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          handleCellClick(path, field, value);
        }}
        style={{
          cursor: "text",
          padding: isSubdivisionNameCell ? "0" : "5px",
          marginLeft: isSubdivisionNameCell ? "-16px" : "0",
          paddingLeft: isSubdivisionNameCell ? "16px" : "0",
          color: !value ? "#bfbfbf" : "inherit",
        }}
        className={className}
      >
        {value || (isSubdivisionNameCell ? "" : "Click to edit")}
      </div>
    );
  };

  const divisionDisplayCode = selectedDivision
    ? selectedDivision.substring(0, 2)
    : null;
  const divisionDisplayName = divisionDisplayCode
    ? `${divisionDisplayCode} - ${
        divisions[divisionDisplayCode] || selectedDivision
      }`
    : selectedDivision;

  const handleAccept = (record) => {
    const { divisionKey, subdivisionKey, dndId } = record;

    setTableState((prev) => {
      const next = { ...prev };
      if (!next[divisionKey] || !next[divisionKey][subdivisionKey]) {
        return prev;
      }

      const items = [...next[divisionKey][subdivisionKey]];
      const itemIndex = items.findIndex((item) => item.dndId === dndId);
      if (itemIndex === -1) return prev;

      items[itemIndex] = { ...items[itemIndex], isPending: false };
      next[divisionKey] = { ...next[divisionKey], [subdivisionKey]: items };

      handleSaveLatestSubmittals(next);
      return next;
    });
  };

  const handleDelete = (record) => {
    const { divisionKey, subdivisionKey, dndId } = record;

    setTableState((prev) => {
      const next = { ...prev };
      if (!next[divisionKey] || !next[divisionKey][subdivisionKey]) {
        return prev;
      }

      const filteredItems = next[divisionKey][subdivisionKey].filter(
        (item) => item.dndId !== dndId,
      );

      next[divisionKey] = {
        ...next[divisionKey],
        [subdivisionKey]: filteredItems,
      };

      if (filteredItems.length === 0) {
        const { [subdivisionKey]: _, ...rest } = next[divisionKey];
        next[divisionKey] = rest;
        setActiveSubdivisionKeys((keys) =>
          keys.filter((k) => k !== subdivisionKey),
        );
      }

      handleSaveLatestSubmittals(next);
      return next;
    });
  };

  // --- COLUMN DEFINITIONS ---
  const getSubmittalColumns = (subdivisionName) => [
    {
      key: "dragHandle",
      width: 40,
      align: "center",
      render: () => <HolderOutlined style={{ color: "#bfbfbf" }} />,
    },
    {
      title: "Submittal Name",
      dataIndex: "specTitle",
      width: 300,
      render: (val, record) => (
        <EditableCell
          value={val}
          path={[selectedDivision, subdivisionName, record.dndId]}
          field="specTitle"
        />
      ),
    },
    {
      title: "Submittal Number",
      dataIndex: "specNumber",
      render: (val, record) => (
        <EditableCell
          value={val}
          path={[selectedDivision, subdivisionName, record.dndId]}
          field="specNumber"
        />
      ),
    },
    {
      title: "Submittal Revision",
      dataIndex: "specRevision",
      render: (val, record) => (
        <EditableCell
          value={val}
          path={[selectedDivision, subdivisionName, record.dndId]}
          field="specRevision"
        />
      ),
    },
    {
      title: "Submittal Type",
      dataIndex: "specType",
      render: (val, record) => (
        <EditableCell
          value={val}
          path={[selectedDivision, subdivisionName, record.dndId]}
          field="specType"
        />
      ),
    },
    {
      title: "Page",
      dataIndex: "page",
      render: (val) =>
        val !== undefined && val !== null && val !== "" ? val : "-",
    },
    {
      title: "Actions",
      dataIndex: "actions",
      align: "center",
      render: (val, record) => {
        const fullRecordContext = {
          ...record,
          divisionKey: selectedDivision,
          subdivisionKey: subdivisionName,
        };

        return (
          <div
            style={{ display: "flex", gap: "8px" }}
            className="justify-content-end"
          >
            {record.isPending ? (
              <>
                <Tooltip title="Accept">
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={() => handleAccept(fullRecordContext)}
                  />
                </Tooltip>
                <Tooltip title="Decline">
                  <Button
                    danger
                    type="primary"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => handleDelete(fullRecordContext)}
                  />
                </Tooltip>
              </>
            ) : (
              <Button
                type="link"
                shape="circle"
                onClick={() => handleDelete(fullRecordContext)}
              >
                <Tooltip title="Delete Submittal">
                  <DeleteOutlined className="link-danger" />
                </Tooltip>
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const onMoveSubdivision = (fromDivision, toDivisionCode, subdivisionName) => {
    if (!fromDivision || !toDivisionCode || !subdivisionName) return;

    setTableState((prev) => {
      if (!prev[fromDivision] || !prev[fromDivision][subdivisionName]) {
        return prev;
      }

      const targetDivisionKey =
        Object.keys(prev).find((key) => key.startsWith(toDivisionCode)) ||
        toDivisionCode;

      if (fromDivision === targetDivisionKey) return prev;

      const next = { ...prev };
      const subdivisionData = prev[fromDivision][subdivisionName];

      const { [subdivisionName]: _, ...remainingFromDivision } =
        prev[fromDivision];

      next[fromDivision] = remainingFromDivision;
      next[targetDivisionKey] = {
        ...(prev[targetDivisionKey] || {}),
        [subdivisionName]: subdivisionData,
      };

      handleSaveLatestSubmittals(next);
      return next;
    });

    setActiveSubdivisionKeys((prev) =>
      prev.filter((key) => key !== subdivisionName),
    );
  };

  useEffect(() => {
    return () => {
      setTableState({});
    };
  }, []);

  const handleSaveLatestSubmittals = async (latestState = null) => {
    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");

    const stateToSave = latestState || tableState;

    try {
      const formattedSubmittals = {};

      Object.keys(stateToSave || {}).forEach((divisionKey) => {
        const divisionData = stateToSave[divisionKey] || {};

        if (!formattedSubmittals[divisionKey])
          formattedSubmittals[divisionKey] = {};

        Object.keys(divisionData).forEach((subdivisionKey) => {
          const items = divisionData[subdivisionKey] || [];
          formattedSubmittals[divisionKey][subdivisionKey] = items.map(
            ({ dndId, divisionKey, subdivisionKey, ...rest }) => ({
              ...rest,
              isPending: rest.isPending === undefined ? true : rest.isPending,
            }),
          );
        });
      });

      await HilyteService.updatePDFInfo({
        company: company_id,
        loginEmail,
        code,
        pdfID,
        sections: existingSections,
        submittals: formattedSubmittals,
      });

      message.success(`Submittals saved successfully`);
    } catch (err) {
      console.error("Failed to update PDF info:", err);
      message.error("Failed to save submittals");
    }
  };

  useEffect(() => {
    if (!pdfID) return;

    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");

    setTableState({});
    setSelectedDivision(null);
    setActiveSubdivisionKeys([]);
    setIsLoadingSubmittals(true);

    HilyteService.getPDF({
      company: company_id,
      loginEmail,
      code,
      pdfID,
    })
      .then((res) => {
        if (res?.submittals && Object.keys(res.submittals).length > 0) {
          const processed = processData(res.submittals, false);
          setTableState(processed);
        }
      })
      .catch((err) => {
        console.error("Failed to load saved submittals:", err);
      })
      .finally(() => {
        setIsLoadingSubmittals(false); // ← done loading
      });
  }, [pdfID]);

  // --- Accept/Reject All Helper Logic ---
  const hasPendingSubmittals = useMemo(() => {
    return Object.values(tableState || {}).some((division) =>
      Object.values(division || {}).some((items) =>
        (items || []).some((item) => item.isPending),
      ),
    );
  }, [tableState]);

  const handleAcceptAll = () => {
    setTableState((prev) => {
      const next = { ...prev };

      Object.keys(next).forEach((divisionKey) => {
        Object.keys(next[divisionKey] || {}).forEach((subdivisionKey) => {
          next[divisionKey][subdivisionKey] = (
            next[divisionKey][subdivisionKey] || []
          ).map((item) => ({ ...item, isPending: false }));
        });
      });

      handleSaveLatestSubmittals(next);
      return next;
    });
  };

  const handleRejectAll = () => {
    setTableState((prev) => {
      const next = {};

      Object.keys(prev).forEach((divisionKey) => {
        const divisionData = prev[divisionKey] || {};
        const filteredDivision = {};

        Object.keys(divisionData).forEach((subdivisionKey) => {
          const remaining = (divisionData[subdivisionKey] || []).filter(
            (item) => !item.isPending,
          );

          if (remaining.length > 0) {
            filteredDivision[subdivisionKey] = remaining;
          }
        });

        if (Object.keys(filteredDivision).length > 0) {
          next[divisionKey] = filteredDivision;
        }
      });

      handleSaveLatestSubmittals(next);
      return next;
    });
  };

  if (isLoadingSubmittals) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          maxHeight: "800px",
          background: "#fff",
          gap: "12px",
          color: "#8c8c8c",
          fontSize: "16px",
        }}
        className="rounded"
      >
        <LoadingComponent />
        <span>Loading submittals…</span>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        maxHeight: "800px",
        background: "#fff",
      }}
      className="rounded"
    >
      {/* Left Sidebar */}
      <div
        style={{
          width: "320px",
          background: "#fff",
          borderRight: "1px solid #d9d9d9",
          overflowY: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "8px 16px 8px 16px",
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <Input
            prefix={<SearchOutlined className="opacity-25" />}
            placeholder="Search Divisions"
            allowClear
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: "100%" }}
            className="p-2"
          />
        </div>
        <div style={{ padding: "8px", overflowY: "auto" }}>
          {filteredDivisions.map(([code, name]) => {
            const divisionKey =
              Object.keys(tableState).find((key) => key.startsWith(code)) ||
              code;
            const isSelected = selectedDivision === divisionKey;

            const divisionData = tableState[divisionKey] || {};

            const { count, hasPendingItems } = Object.values(
              divisionData,
            ).reduce(
              (acc, arr) => {
                if (Array.isArray(arr)) {
                  acc.count += arr.length;
                  if (
                    !acc.hasPendingItems &&
                    arr.some((item) => item.isPending)
                  ) {
                    acc.hasPendingItems = true;
                  }
                }
                return acc;
              },
              { count: 0, hasPendingItems: false },
            );

            const badgeColor = hasPendingItems
              ? "red"
              : isSelected
                ? "blue"
                : "gray";

            return (
              <div
                key={code}
                onClick={() => handleDivisionClick(code)}
                style={{
                  padding: "12px 16px",
                  marginBottom: "4px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  background: isSelected ? "#1890ff" : "transparent",
                  color: isSelected ? "#fff" : "#000",
                  transition: "all 0.3s",
                  display: "flex",
                  gap: "0.8rem",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.background = "#f0f0f0";
                }}
                onMouseLeave={(e) => {
                  if (!isSelected)
                    e.currentTarget.style.background = "transparent";
                }}
              >
                <FolderFilled className="opacity-50" />
                <div className="d-flex justify-content-between align-items-center w-100">
                  <div style={{ fontWeight: 400, fontSize: "1rem" }}>
                    {code} - {name}
                  </div>
                  <div>
                    <Badge count={count} showZero color={badgeColor} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
        {selectedDivision ? (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h1 style={{ fontSize: "24px", fontWeight: "bold" }}>
                {divisionDisplayName}
              </h1>
              <div className="d-flex align-items-center gap-2">
                {(() => {
                  const hasPending =
                    selectedDivision &&
                    Object.values(tableState[selectedDivision] || {}).some(
                      (items) =>
                        Array.isArray(items) && items.some((i) => i.isPending),
                    );
                  return hasPending ? (
                    <>
                      <Button
                        size="large"
                        type="primary"
                        onClick={() => {
                          setTableState((prev) => {
                            const next = { ...prev };
                            const divData = { ...next[selectedDivision] };
                            Object.keys(divData).forEach((subKey) => {
                              divData[subKey] = divData[subKey].map((item) =>
                                item.isPending
                                  ? { ...item, isPending: false }
                                  : item,
                              );
                            });
                            next[selectedDivision] = divData;
                            handleSaveLatestSubmittals(next);
                            return next;
                          });
                        }}
                      >
                        <CheckOutlined /> Accept All
                      </Button>
                      <Button
                        size="large"
                        danger
                        onClick={() => {
                          setTableState((prev) => {
                            const next = { ...prev };
                            const divData = { ...next[selectedDivision] };
                            const emptySubKeys = [];
                            Object.keys(divData).forEach((subKey) => {
                              const filtered = divData[subKey].filter(
                                (item) => !item.isPending,
                              );
                              divData[subKey] = filtered;
                              if (filtered.length === 0)
                                emptySubKeys.push(subKey);
                            });
                            emptySubKeys.forEach((subKey) => {
                              delete divData[subKey];
                            });
                            setActiveSubdivisionKeys((keys) =>
                              keys.filter((k) => !emptySubKeys.includes(k)),
                            );
                            next[selectedDivision] = divData;
                            handleSaveLatestSubmittals(next);
                            return next;
                          });
                        }}
                      >
                        <CloseOutlined /> Reject All
                      </Button>
                    </>
                  ) : null;
                })()}
                <Button
                  type="primary"
                  size="large"
                  onClick={() => setIsAddSubdivisionModalOpen(true)}
                >
                  <PlusOutlined /> Add Subdivision
                </Button>
              </div>
            </div>

            {subdivisionKeys.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={subdivisionKeys}
                  strategy={verticalListSortingStrategy}
                >
                  {subdivisionKeys.map((subName) => {
                    const submittals = currentDivisionData[subName] || [];
                    const isExpanded = activeSubdivisionKeys.includes(subName);

                    return (
                      <SortableSubdivisionPanel
                        key={subName}
                        id={subName}
                        subdivisionName={subName}
                        currentDivision={selectedDivision}
                        isExpanded={isExpanded}
                        onToggle={() => toggleSubdivision(subName)}
                        panelStyle={panelStyle}
                        onAddSubmittal={(name) => {
                          setSubdivisionForNewSubmittal(name);
                          setIsAddSubmittalModalOpen(true);
                        }}
                        onDeleteSubdivision={(name) => {
                          setPendingDeleteSubdivision(name);
                        }}
                        onMoveSubdivision={onMoveSubdivision}
                        divisions={divisions}
                        headerContent={
                          <div className="w-50">
                            <EditableCell
                              value={subName}
                              path={[selectedDivision, subName]}
                              field="name"
                            />
                          </div>
                        }
                      >
                        <SortableContext
                          items={submittals.map((s) => s.dndId)}
                          strategy={verticalListSortingStrategy}
                        >
                          <Table
                            rowKey="dndId"
                            dataSource={submittals}
                            columns={getSubmittalColumns(subName)}
                            pagination={false}
                            style={{ background: "#fff" }}
                            components={{ body: { row: SortableRow } }}
                          />
                        </SortableContext>
                      </SortableSubdivisionPanel>
                    );
                  })}
                </SortableContext>

                <DragOverlay
                  dropAnimation={{
                    sideEffects: defaultDropAnimationSideEffects({
                      styles: { active: { opacity: "0.5" } },
                    }),
                  }}
                >
                  {activeDragItem ? (
                    activeDragItem.type === "subdivision" ? (
                      <div
                        style={{
                          ...panelStyle,
                          padding: "12px",
                          background: "#FAFAFA",
                        }}
                      >
                        {activeDragItem.display}
                      </div>
                    ) : (
                      <div
                        className="border border-1 rounded"
                        style={{ padding: "12px", background: "#fff" }}
                      >
                        {activeDragItem.display}
                      </div>
                    )
                  ) : null}
                </DragOverlay>
              </DndContext>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "calc(100% - 60px)",
                  color: "#8c8c8c",
                }}
              >
                <Empty
                  description={
                    <p style={{ fontSize: "16px" }}>
                      No submittals found in this division.
                    </p>
                  }
                />
              </div>
            )}
          </>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#8c8c8c",
            }}
          >
            <Empty
              description={
                <p style={{ fontSize: "16px" }}>
                  Select a division from the sidebar to view submittals.
                </p>
              }
            />
          </div>
        )}
      </div>

      <ReviewPDFModals
        onSubmittalsChanged={handleSaveLatestSubmittals}
        isAddSubmittalModalOpen={isAddSubmittalModalOpen}
        setIsAddSubmittalModalOpen={setIsAddSubmittalModalOpen}
        newSubmittalName={newSubmittalName}
        setNewSubmittalName={setNewSubmittalName}
        subdivisionForNewSubmittal={subdivisionForNewSubmittal}
        setSubdivisionForNewSubmittal={setSubdivisionForNewSubmittal}
        isAddSubdivisionModalOpen={isAddSubdivisionModalOpen}
        setIsAddSubdivisionModalOpen={setIsAddSubdivisionModalOpen}
        newSubdivisionName={newSubdivisionName}
        setNewSubdivisionName={setNewSubdivisionName}
        pendingDeleteSubdivision={pendingDeleteSubdivision}
        setPendingDeleteSubdivision={setPendingDeleteSubdivision}
        selectedDivision={selectedDivision}
        setTableState={setTableState}
        setActiveSubdivisionKeys={setActiveSubdivisionKeys}
      />
    </div>
  );
}
